<template>
  <div class="shopDetail">
    <div v-if="isLoading" class="visuallyHidden" role="status" aria-live="polite">{{ $t('text.checkoutFinishedIFrameRedirecting') }}</div>
    <div v-if="error" class="visuallyHidden" role="alert" aria-live="assertive">{{ $t('notification.genericError') }}</div>
    <div class="shopDetailCard">
      <div class="shopDetailImage">
        <img :src="shopData && shopData.image ? shopData.image : ''" :alt="shopData.name" class="imageCoversParent"/>
      </div>
      <div class="shopDetailText text-left">
        <div class="shopDetailsLabel">{{ $t('shopDetails.label.contact') }}</div>
        <div v-html="safeFormattedAddress" class="text-left"></div>
        <div v-html="safeCustom6"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { shopService } from '@/services/api/shopService';

export default {
  props: {
    shopId: {
      type: [String, Number],
      required: false
    },
    shop: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      shopData: this.shop || {},
      isLoading: false,
      error: null
    };
  },
  watch: {
    shopId(newId) {
      if (!this.shop && newId) {
        this.loadShop(newId);
      }
    },
    shop(newShop) {
      if (newShop) {
        this.shopData = newShop;
      }
    }
  },
  computed: {
    safeFormattedAddress() {
      return this.sanitizeShopHtml(this.shopData && this.shopData.formattedAddress ? this.shopData.formattedAddress : '');
    },
    safeCustom6() {
      return this.sanitizeShopHtml(this.shopData && this.shopData.custom6 ? this.shopData.custom6 : '');
    }
  },
  methods: {
    async loadShop(id) {
      this.isLoading = true;
      try {
        const result = await shopService.getShop(id);
        this.shopData = result.shop || {};
      } catch (e) {
        this.error = e;
      } finally {
        this.isLoading = false;
      }
    },
    sanitizeShopHtml(rawHtml) {
      if (!rawHtml || typeof rawHtml !== 'string') {
        return '';
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${rawHtml}</div>`, 'text/html');
      const root = doc.body.firstElementChild;
      const allowedTags = new Set(['A', 'B', 'BR', 'EM', 'I', 'LI', 'OL', 'P', 'SPAN', 'STRONG', 'UL']);

      if (!root) {
        return '';
      }

      const sanitizeNode = (node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) {
          return;
        }

        const element = node;
        const tagName = element.tagName;

        Array.from(element.children).forEach((child) => sanitizeNode(child));

        if (!allowedTags.has(tagName)) {
          const parent = element.parentNode;
          if (!parent) {
            return;
          }

          while (element.firstChild) {
            parent.insertBefore(element.firstChild, element);
          }
          parent.removeChild(element);
          return;
        }

        Array.from(element.attributes).forEach((attr) => {
          const attrName = attr.name.toLowerCase();
          const attrValue = (attr.value || '').trim();

          const isAllowedAnchorAttr = tagName === 'A' && ['href', 'target', 'rel'].includes(attrName);
          if (!isAllowedAnchorAttr) {
            element.removeAttribute(attr.name);
            return;
          }

          if (attrName === 'href' && !/^(https?:|mailto:|tel:|\/)/i.test(attrValue)) {
            element.removeAttribute('href');
          }

          if (attrName === 'target' && attrValue.toLowerCase() !== '_blank') {
            element.removeAttribute('target');
          }
        });

        if (tagName === 'A' && element.getAttribute('target') === '_blank') {
          element.setAttribute('rel', 'noopener noreferrer');
        }
      };

      Array.from(root.children).forEach((child) => sanitizeNode(child));
      return root.innerHTML;
    },
  },
  created() {
    if (!this.shop && this.shopId) {
      this.loadShop(this.shopId);
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/css/customizer';

.shopDetailCard {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.shopDetailImage {
  width: 100%;
  height: 200px;
  flex-shrink: 0;
}
.shopDetailImage > img {
  border-radius: chain(shop-details-image-border-radius, image-border-radius, --border-radius);
}

.shopDetailText {
  width: 100%;
}

.shopDetailsLabel {
  font-weight: 600;
}

@media (min-width: 768px) {
  .shopDetailCard {
    flex-direction: row;
    align-items: stretch;
    gap: 1.5rem;
  }

  .shopDetailImage {
    width: 40%;
    max-width: 360px;
    height: fit-content;
  }

  .shopDetailText {
    width: 60%;
  }
}

.visuallyHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
